import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import {
  type MovementPattern,
  type PlanStarter,
  type Workout as WorkoutT,
} from '@jackedlogs/shared';

import { rowIdOf } from './state/loggerDraft';
import { LoggerHeader } from './LoggerHeader';
import { groupMembersOf } from './supersetNotebook';
import { useCoachNotes } from './useCoachNotes';
import { HoldCountdownOverlay } from './HoldCountdownOverlay';
import { LoggerModals } from './LoggerModals';
import { usePlanCues } from './usePlanCues';
import { useLoggerScreen } from './useLoggerScreen';
import { useLoggerCursor } from './useLoggerCursor';
import { useFieldFocusSignal } from './useFieldFocus';
import { useRestAdvance } from './useRestAdvance';
import { LoggerContext } from './loggerContext';
import { EntryHistoryProvider } from './useEntryHistory';
import { WorkoutMetaFooter } from './WorkoutMetaFooter';
import { TimelineLoggerScreen } from './TimelineLoggerScreen';
import type { LoggerHostDeps } from './hostDeps';

export type EditingTarget = {
  /** Existing session id to delete on successful save. persistWorkout
   *  follows the write-then-cleanup model: a fresh id is created, sets
   *  posted, then the old session is dropped. */
  sessionId: string;
  /** Pre-loaded workout shape (assemble of session + sets) used to seed
   *  the draft. */
  workout: WorkoutT;
  /** Per-slug movement-pattern map captured at the time the parent
   *  fetched the catalog for the edit. Optional: when missing, edit-
   *  mode saves still succeed but the post-save MAX-bump check skips
   *  entries with unknown patterns (they fall through the rep-trust
   *  cap default). */
  patternsBySlug?: Record<string, MovementPattern | null>;
  /** 'edit' (default) overwrites the existing session via the write-
   *  then-cleanup path. 'repeat' clones the structure into a brand-
   *  new session — the source row is left untouched, completedAt /
   *  fail are blanked, and startedAt resets to now. */
  mode?: 'edit' | 'repeat';
};

/** Payload the LoggerScreen produces on a successful direct save so the
 *  parent can run the MAX-bump evaluation against the just-saved data.
 *  Undefined for queued saves (the workout reached the outbox, not the
 *  server, so any "new MAX" check would be premature). */
export type MaxBumpInput = {
  workout: WorkoutT;
  exercisePatterns: Record<string, MovementPattern | null | undefined>;
  workoutId: string;
  /** Best qualifying e1RM per exercise over the recent corroboration
   *  window, snapshotted from the pre-save history cache. Gates the
   *  downward MAX-regression prompt so one light day can't lower a MAX
   *  below a recent stronger session (Antenore 2026-06-26). */
  recentBestE1RM?: Record<string, number>;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  /** When set, the Logger opens pre-populated with the target workout
   *  and saves an edit (write-then-cleanup of the old session). */
  editing?: EditingTarget | null;
  /** When set, the Logger opens pre-populated from a plan starter
   *  (buildPlanStarter output). Plan id + name + notes ride through
   *  to the save payload so the server can flip the plan row to
   *  status=completed. Mutually exclusive with `editing` — call sites
   *  must not pass both; if both are set, editing wins (matches the
   *  web `pendingStart` precedence rule). */
  planStarter?: PlanStarter | null;
  /** Fired after a successful save (direct or queued). Caller refreshes
   *  the workouts list and the outbox-derived counter. The optional
   *  maxBumpInput is populated only on `'sent'` saves; consumers feed
   *  it to `useMaxBumpCandidates.evaluate`. */
  onSaved: (kind: 'sent' | 'queued', maxBumpInput?: MaxBumpInput) => void;
  /** Fired when the user taps the header minimize affordance. The
   *  Logger's draft + outbox state stay alive — the caller toggles
   *  `visible` to false so the system Modal hides, then surfaces a
   *  Resume pill (rendered by TabShell) so the user can navigate the
   *  other tabs and come back. Optional: when omitted, the minimize
   *  button is not rendered. */
  onMinimize?: () => void;
  /** Deep-link to Settings → Body for the once-only bodyweight nudge
   *  (D1-S2). The parent owns it because the jump is cross-tab. */
  onRequestSetBodyweight?: () => void;
  /** RW-18 host seam — everything the shared tree cannot reach itself
   *  (Profile, durable write, the exercise-catalog cache, the
   *  host-specific picker/timer-sheet/effort-scale components). See
   *  hostDeps.ts. Mobile's mount supplies its existing modules
   *  unchanged; web-rnw's supplies its own equivalents. */
  deps: LoggerHostDeps;
};

export function LoggerScreen({
  visible,
  onClose,
  editing = null,
  planStarter = null,
  onSaved,
  onMinimize,
  onRequestSetBodyweight,
  deps,
}: Props) {
  // The full screen bag drives both this orchestration shell and the
  // timeline subtree. LoggerScreen destructures what it renders directly
  // (header / footer / modals); TimelineLoggerScreen reads
  // the same bag through LoggerContext.
  const base = useLoggerScreen({
    visible,
    onClose,
    editing,
    planStarter,
    onSaved,
    onRequestSetBodyweight,
    deps,
  });
  // Focus cursor lives HERE (above the Modal) so it survives minimize -> resume
  // (LG-13); merged into the context bag the timeline subtree reads. Keyed on
  // the session so a new workout resets it. See useLoggerCursor.
  const cursor = useLoggerCursor(base.draft.startedAt);
  // The post-log focus transition lives up here for the SAME reason the cursor
  // does (LG-13): it holds a decision that has been taken but not yet applied,
  // and minimize unmounts everything below the Modal. Held inside, a minimize
  // during a rest would destroy the pending transition while the timer — also
  // hoisted — kept counting, and the focus would simply never advance. It moves
  // the cursor, so it belongs beside the cursor.
  // Which field holds the keyboard, owned up here for the same LG-13 reason:
  // the rest-end resume that reads it runs in this body, and the fields that
  // report into it live below the Modal.
  const fieldFocus = useFieldFocusSignal();
  const restAdvance = useRestAdvance({
    restTimer: base.restTimer,
    cursor,
    anyFieldFocused: fieldFocus.anyFieldFocused,
  });
  const ctx = { ...base, ...cursor, restAdvance, fieldFocus };
  const {
    theme,
    t,
    i18n,
    styles,
    draft,
    dispatch,
    defaultUnit,
    energyReadout,
    postSaveSummary,
    summarySessionId,
    sessionResult,
    summaryCardRef,
    headerSharing,
    triggerHeaderShare,
    pickerOpen,
    setPickerOpen,
    effortScaleOpen,
    setEffortScaleOpen,
    swapEntryIdx,
    setSwapEntryIdx,
    tipEntryIdx,
    setTipEntryIdx,
    catalog,
    elapsedLabel,
    hold,
    startManualRest,
    restPicker,
    timerDefaults,
    groupPickerEntryIdx,
    setGroupPickerEntryIdx,
    supersetMenu,
    setSupersetMenu,
    addToGroupId,
    setAddToGroupId,
    error,
    serverError,
    usedSlugs,
    groupCatalog,
    plate,
    addMany,
    createSuperset,
    submitting,
    onSave,
    onCancel,
    totals,
    mode,
  } = ctx;
  // Per-exercise plan instruction for the "?" tip — resolved per-slug from the
  // plan (same source as the FocusPanel cue), since it's no longer carried on
  // the draft. The tip entry is always the active entry.
  const planCues = usePlanCues(draft.fromPlanId ?? draft.coachNotesRef, deps.apiFetch);
  const tipPlanNote =
    tipEntryIdx !== null
      ? planCues.get(draft.entries[tipEntryIdx]?.exerciseId ?? '')?.instruction
      : undefined;
  // Coach notes for the header "?" — captured at Start (draft.coachNotes,
  // offline-safe), with a render-time fallback that re-resolves from the
  // program/plan so resumed / pre-3.14 / edited / repeated sessions also
  // surface the "?" (FK + resolve at render, no snapshot). The reference is
  // `fromPlanId` for a live plan-started draft, or `coachNotesRef` for a
  // past session re-opened in Edit/Repeat (hydrateFromWorkout copies it from
  // the saved Workout.fromPlanId — display-only, never the save id).
  const coachNotes = useCoachNotes(
    draft.fromPlanId ?? draft.coachNotesRef,
    draft.coachNotes,
    deps.apiFetch,
  );
  // The header clock lights up inside a superset (MX-5 D9): there the automatic
  // rest is the 4s implement change, so "start a real rest" is the one moment
  // that affordance answers a live question. Resolved with the SAME pure helper
  // the timeline subtree uses — the header renders above LoggerContext, so it
  // can't read the subtree's copy, but it must not grow a second rule either.
  const inSuperset =
    groupMembersOf(
      draft.entries,
      draft.entries.find((e) => rowIdOf(e) === cursor.activeId)?.group?.id,
    ).length >= 2;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onCancel}
      presentationStyle="pageSheet"
    >
      {/* Modal hosts a separate native view tree on Android that the
          root-level GestureHandlerRootView in App.tsx doesn't reach, so we
          re-root one here. Kept as the standard gesture root for any
          gesture-driven child (the timeline + future surfaces). */}
      <GestureHandlerRootView style={styles.gestureRoot}>
      <EntryHistoryProvider catalog={catalog} apiFetch={deps.apiFetch}>
      <LoggerContext.Provider value={ctx}>
      <SafeAreaView
        style={[styles.safe, { backgroundColor: theme.palette.bg }]}
        edges={['top', 'left', 'right', 'bottom']}
      >
        <LoggerHeader
          postSaveSummary={postSaveSummary}
          mode={mode}
          elapsedLabel={elapsedLabel}
          hasSessionResult={sessionResult.result !== null}
          headerSharing={headerSharing}
          submitting={submitting}
          entriesLength={draft.entries.length}
          onMinimize={onMinimize}
          onSave={onSave}
          onCancel={onCancel}
          triggerHeaderShare={triggerHeaderShare}
          onStartRest={startManualRest}
          inSuperset={inSuperset}
          coachNotes={coachNotes}
          styles={styles}
          theme={theme}
          t={t}
        />

        {/* The rest banner used to be here, and the hold/cardio timer had one
            of its own beside it. Neither exists any more (MX-5 slices 3 and
            3b): both timers run inside the effort meter's two rows, where the
            athlete pressed. The rest countdown IS the transition to the next
            set now, so it also carries the "→ Set 3/4" caption that used to
            flash on the card for a beat and vanish. Two rows less on screen
            always, three while a timer runs — which is exactly when the screen
            is fullest. */}
        {/* Active logging happens on the timeline (the sole logger). After a
            'sent' save we switch to summary mode: the post-session card +
            CTAs render in a plain scroll view (no autoscroll needed). */}
        {postSaveSummary === null ? (
          <TimelineLoggerScreen />
        ) : (
        <KeyboardAvoidingView
          style={styles.flex}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <ScrollView
            contentContainerStyle={styles.body}
            keyboardShouldPersistTaps="handled"
          >
            <WorkoutMetaFooter
              postSaveSummary={postSaveSummary}
              summarySessionId={summarySessionId}
              sessionResultValue={sessionResult.result}
              energyReadout={energyReadout}
              totals={totals}
              draft={draft}
              error={error}
              serverError={serverError}
              headerSharing={headerSharing}
              triggerHeaderShare={triggerHeaderShare}
              dispatch={dispatch}
              defaultUnit={defaultUnit}
              locale={i18n.language}
              summaryCardRef={summaryCardRef}
              styles={styles}
              theme={theme}
              t={t}
              PostSessionSummaryCard={deps.PostSessionSummaryCard}
              profile={deps.profile}
            />
          </ScrollView>
        </KeyboardAvoidingView>
        )}

        <LoggerModals
          animationSource={deps.animationSource}
          pickerOpen={pickerOpen}
          setPickerOpen={setPickerOpen}
          addMany={addMany}
          createSuperset={createSuperset}
          usedSlugs={usedSlugs}
          swapEntryIdx={swapEntryIdx}
          setSwapEntryIdx={setSwapEntryIdx}
          effortScaleOpen={effortScaleOpen}
          setEffortScaleOpen={setEffortScaleOpen}
          tipEntryIdx={tipEntryIdx}
          setTipEntryIdx={setTipEntryIdx}
          tipPlanNote={tipPlanNote}
          groupPickerEntryIdx={groupPickerEntryIdx}
          setGroupPickerEntryIdx={setGroupPickerEntryIdx}
          supersetMenu={supersetMenu}
          setSupersetMenu={setSupersetMenu}
          addToGroupId={addToGroupId}
          setAddToGroupId={setAddToGroupId}
          catalog={catalog}
          entries={draft.entries}
          currentMaxes={deps.maxesLoaded ? deps.maxMeta : null}
          location={draft.location ?? null}
          dispatch={dispatch}
          plate={plate}
          restPicker={restPicker}
          timerDefaults={timerDefaults}
          groupCatalog={groupCatalog}
          defaultUnit={defaultUnit}
          theme={theme}
          t={t}
          ExercisePickerMount={deps.ExercisePickerMount}
          ExerciseTimerSheetMount={deps.ExerciseTimerSheet}
          EffortScalePicker={deps.EffortScalePicker}
          saveExerciseGroup={deps.saveExerciseGroup}
          profile={deps.profile}
          PainDot={deps.PainDot}
          usePainRiskLabel={deps.usePainRiskLabel}
          patchProfile={deps.patchProfile}
          notify={deps.notify}
          confirm={deps.confirm}
        />

        {/* 3-2-1-GO intro for the hold timer — full-screen overlay, on top. */}
        <HoldCountdownOverlay intro={hold.intro} />
      </SafeAreaView>
      </LoggerContext.Provider>
      </EntryHistoryProvider>
      </GestureHandlerRootView>
    </Modal>
  );
}

